import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { portalPublicAPI } from "../api/client";
import "./CustomerPortalPage.css";

/*
  The customer-facing order tracker behind a share link.

  Rendered outside ModuleRouter and outside AuthProvider, so it never mounts the
  app shell or reads a staff session. Everything it shows comes from the single
  public endpoint, which decides what a customer is allowed to see.
*/

function formatDate(value) {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function Stage({ stage }) {
  const total = stage.completed + stage.remaining;
  const pct = total > 0 ? Math.round((stage.completed / total) * 100) : 0;
  return (
    <div className={`cp-stage ${stage.status}`}>
      <div className="cp-stage-name" title={stage.name}>{stage.name}</div>
      <div className="cp-stage-bar">
        <div className="cp-stage-fill" style={{ width: `${pct}%` }} />
      </div>
      <div className="cp-stage-count">
        {stage.status === "pending"
          ? "Not started"
          : stage.status === "skipped"
            ? "Not required"
            : `${stage.completed} of ${total} done`}
      </div>
    </div>
  );
}

function PurchaseOrder({ po }) {
  return (
    <div className="cp-po">
      <div className="cp-po-head">
        <div>
          <div className="cp-po-number">{po.poNumber}</div>
          <div className="cp-po-meta">
            {po.totalQuantity.toLocaleString()} unit{po.totalQuantity === 1 ? "" : "s"}
            {" · "}Due {formatDate(po.deliveryDate)}
          </div>
        </div>
        <span className={`cp-badge ${po.status}`}>{po.statusLabel}</span>
      </div>

      {po.parts.map((part) => (
        <div className="cp-part" key={part.id}>
          <div className="cp-part-head">
            <span className="cp-part-name">{part.name}</span>
            <span className="cp-part-qty">
              {part.quantity.toLocaleString()} unit{part.quantity === 1 ? "" : "s"}
              {part.stageCount > 0 && ` · ${part.stagesCompleted}/${part.stageCount} stages complete`}
            </span>
          </div>
          {part.stages.length > 0 ? (
            <div className="cp-stages">
              {part.stages.map((stage) => (
                <Stage key={`${part.id}-${stage.order}-${stage.name}`} stage={stage} />
              ))}
            </div>
          ) : (
            <div className="cp-stage-count">Production has not started on this item yet.</div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function CustomerPortalPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const requestedRef = useRef(null);

  useEffect(() => {
    // React StrictMode mounts effects twice in development. Guarding on the token
    // rather than a bare boolean means a genuinely new link still refetches, but
    // the same link is only ever requested — and so only ever counted — once.
    //
    // Deliberately no cleanup/cancelled flag here: StrictMode's unmount would set
    // it on the only in-flight request, while the remount skips past this guard
    // and starts nothing, leaving the page loading forever. Settling into an
    // unmounted component is a harmless no-op in React 18; never settling is not.
    if (requestedRef.current === token) return;
    requestedRef.current = token;

    portalPublicAPI
      .get(token)
      .then((res) => setData(res.data.data))
      .catch((err) => setError(err.response?.data?.error || "This link could not be opened"))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    if (data?.customerName) document.title = `Order Status — ${data.customerName}`;
  }, [data]);

  if (loading) {
    return (
      <div className="cp-page">
        <div className="cp-state">Loading your orders…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cp-page">
        <div className="cp-state">
          <p className="cp-state-title">Link unavailable</p>
          <p style={{ margin: 0 }}>{error}</p>
          <p style={{ margin: "14px 0 0", fontSize: 13 }}>
            Please contact your supplier for an up-to-date link.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="cp-page">
      <div className="cp-shell">
        <div className="cp-masthead">
          <p className="cp-eyebrow">{data.companyName}</p>
          <h1 className="cp-title">Order Status for {data.customerName}</h1>
          <p className="cp-subtitle">
            Live production progress across your open orders. View only — nothing here can be changed.
          </p>
        </div>

        <div className="cp-summary">
          <div className="cp-stat">
            <div className="cp-stat-value">{data.summary.total}</div>
            <div className="cp-stat-label">Total Orders</div>
          </div>
          <div className="cp-stat">
            <div className="cp-stat-value">{data.summary.inProduction}</div>
            <div className="cp-stat-label">In Production</div>
          </div>
          <div className="cp-stat">
            <div className={`cp-stat-value${data.summary.delayed > 0 ? " delayed" : ""}`}>
              {data.summary.delayed}
            </div>
            <div className="cp-stat-label">Delayed</div>
          </div>
          <div className="cp-stat">
            <div className="cp-stat-value">{data.summary.completed}</div>
            <div className="cp-stat-label">Completed</div>
          </div>
        </div>

        {data.purchaseOrders.length === 0 ? (
          <div className="cp-empty">There are no orders to show yet.</div>
        ) : (
          data.purchaseOrders.map((po) => <PurchaseOrder key={po.id} po={po} />)
        )}

        <div className="cp-footer">Last updated {formatDate(data.generatedAt)}</div>
      </div>
    </div>
  );
}
